import { supabase } from "@/integrations/supabase/client";

/**
 * Silently logs an error to the error_logs table.
 * Never throws, if the insert fails (e.g. offline), it fails silently.
 */
export async function logError(
  error: unknown,
  componentName: string,
  userId?: string | null,
  actionAttempted?: string,
  userRole?: string | null
) {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    await supabase.from("error_logs").insert({
      user_id: userId ?? null,
      user_role: userRole ?? null,
      error_message: err.message?.slice(0, 2000),
      error_stack: err.stack?.slice(0, 4000) ?? null,
      component_name: componentName,
      action_attempted: actionAttempted ?? null,
      browser_info: navigator.userAgent,
      url: window.location.href,
    });
  } catch {
    // Fail silently, we can't log an error about logging an error
  }
}

/**
 * Check if an error is an auth/session error (401, JWT expired, etc.)
 */
export function isAuthError(error: unknown): boolean {
  if (!error) return false;
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes("JWT expired") ||
    msg.includes("401") ||
    msg.includes("not authenticated") ||
    msg.includes("Invalid Refresh Token") ||
    msg.includes("session_not_found")
  );
}

/**
 * Check if an error is an RLS / permission error
 */
export function isRLSError(error: unknown): boolean {
  if (!error) return false;
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes("row-level security") ||
    msg.includes("new row violates") ||
    msg.includes("permission denied") ||
    msg.includes("403")
  );
}

/**
 * Check if error is a unique constraint violation
 */
export function isDuplicateError(error: unknown): boolean {
  if (!error) return false;
  const msg = error instanceof Error ? error.message : String(error);
  return msg.includes("duplicate key") || msg.includes("unique constraint");
}

/**
 * Get a user-friendly error message based on the error type
 */
export function getFriendlyErrorMessage(error: unknown): string {
  if (isAuthError(error)) {
    return "Your session expired, please log in again to continue.";
  }
  if (isRLSError(error)) {
    return "You don't have permission to do this. Please speak to your manager.";
  }
  if (isDuplicateError(error)) {
    return "It looks like a record with these details already exists. Please check the relevant page to see if it's already been added.";
  }
  return "Something went wrong and your changes couldn't be saved. Please try again. If this keeps happening, speak to your manager.";
}
